import React, { useState } from 'react'
import PropTypes from 'prop-types'

import SendIcon from '@mui/icons-material/Send'
import MailIcon from '@mui/icons-material/Mail'
import DownloadIcon from '@mui/icons-material/Download'

import axios from '../../dataClient/dataClient'
import { handleDownloadResponse } from '../../util/downloadFile'
import { StyledDialog, FlexEndContainer, FlexDialogContent, FlexColumn } from '../styles/Containers'
import { ErrorMessage, WarningMessage } from '../common/UserMessage'
import BodyText, { BoldSpan, BoldFormTitle } from '../styles/Text'
import Button, { SecondaryButton } from '../common/Button'

function SendCommunicationButton({ id, name, onSuccess = () => {}, hasEmail = true }) {
  const [isVisible, setIsVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)
  const [downloadError, setDownloadError] = useState(false)

  const handleOpen = () => setIsVisible(true)
  const handleClose = () => {
    setIsVisible(false)
    setError(false)
    setDownloadError(false)
    setIsLoading(false)
  }

  function download() {
    setDownloadError(false)
    setIsLoading(true)

    const fileName = `${name}.pdf`

    axios
      .get(`/communication/${id}/download`, { responseType: 'blob' })
      .then(response => {
        handleDownloadResponse(response, fileName, 'download')
        handleClose()
      })
      .catch(e => {
        setDownloadError(true)
        console.error('an error occurred while downloading the communication', name, e)
      })
      .finally(() => {
        onSuccess()
        setIsLoading(false)
      })
  }

  async function handleConfirm() {
    setIsLoading(true)
    setError(false)

    axios
      .post(`/communication/${id}/send`)
      .then(() => {
        if (hasEmail) {
          handleClose()
          onSuccess()
        } else {
          download()
        }
      })
      .catch(e => {
        console.error('Failed rejecting estimate', e)
        setError(true)
        setIsLoading(false)
      })
  }

  const errorMessage = hasEmail ? 'There was an error sending the communication.' : 'There was an error marking the communication as sent.'

  return (
    <>
      <Button aria-label="Send Communication" onClick={handleOpen}>
        <SendIcon />
        Send
      </Button>
      <StyledDialog fullWidth open={isVisible} onClose={handleClose} keepMounted={false} maxwidth="700px">
        <FlexDialogContent>
          <FlexColumn>
            <BoldFormTitle>Send: {name}</BoldFormTitle>
            <BodyText>
              Are you sure you want to send the communication <BoldSpan>{name}</BoldSpan>?
            </BodyText>
            {!hasEmail && <WarningMessage>The requestor has not provided an email. You will need to mail them the communication.</WarningMessage>}
            {error && <ErrorMessage>{errorMessage} Please try again.</ErrorMessage>}
            {downloadError && (
              <ErrorMessage>
                Your communication has been marked as sent but there was an error downloading. Please try downloading again.
              </ErrorMessage>
            )}
            {!downloadError ? (
              <FlexEndContainer>
                <SecondaryButton onClick={handleClose} disabled={isLoading}>
                  Cancel
                </SecondaryButton>
                <Button onClick={handleConfirm} loading={isLoading}>
                  {hasEmail ? <SendIcon /> : <MailIcon />}
                  {hasEmail ? 'Send' : 'Download & Mail'}
                </Button>
              </FlexEndContainer>
            ) : (
              <FlexEndContainer>
                <SecondaryButton onClick={handleClose} loading={isLoading}>
                  Close
                </SecondaryButton>
                <Button onClick={download} loading={isLoading}>
                  <DownloadIcon />
                  Download
                </Button>
              </FlexEndContainer>
            )}
          </FlexColumn>
        </FlexDialogContent>
      </StyledDialog>
    </>
  )
}

SendCommunicationButton.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onSuccess: PropTypes.func,
  hasEmail: PropTypes.bool
}

export default SendCommunicationButton
